import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./components/ToastProvider";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TermosPrivacidade from "./pages/TermosPrivacidade";
import Dashboard from "./pages/Dashboard";
import DocumentosList from "./pages/DocumentosList";
import DocumentoForm from "./pages/DocumentoForm";
import DocumentoDetail from "./pages/DocumentoDetail";
import ItensList from "./pages/ItensList";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";
import Verificacoes from "./pages/Verificacoes";
import Funcionarios from "./pages/Funcionarios";
import Envios from "./pages/Envios";
import TrocarSenha from "./pages/TrocarSenha";
import ComingSoon from "./pages/ComingSoon";
import Tickets from "./pages/Tickets";
import AuthCallback from "./pages/AuthCallback";
import Assinaturas from "./pages/Assinaturas";
import Checkout from "./pages/Checkout";
import Planos from "./pages/Planos";
import CheckoutSucesso from "./pages/CheckoutSucesso";

import "./App.css";

// ⚠️ MODO "EM BREVE" - Altere para true quando quiser ativar a landing page
const COMING_SOON_MODE = false;

// Protected Route Component
const ProtectedRoute = ({ children, requirePasswordChange = true }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verificar se deve trocar senha (exceto para a própria página de trocar senha)
  if (requirePasswordChange && user?.deve_trocar_senha) {
    return <Navigate to="/trocar-senha" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Super Admin vai para /super-admin, outros vão para /dashboard
    if (user?.is_super_admin) {
      return <Navigate to="/super-admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  
  // CRITICAL: Detectar session_id durante o render (antes do useEffect)
  // Isso previne race conditions com o Google Auth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Se estiver em modo "Em Breve", mostrar apenas a landing page
  if (COMING_SOON_MODE) {
    return (
      <Routes>
        <Route path="/" element={<ComingSoon />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/registro"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/esqueci-senha"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/redefinir-senha"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/termos"
        element={<TermosPrivacidade />}
      />

      {/* Checkout Público (sem autenticação) */}
      <Route
        path="/planos"
        element={<Planos />}
      />
      <Route
        path="/checkout"
        element={<Checkout />}
      />
      <Route
        path="/checkout/sucesso"
        element={<CheckoutSucesso />}
      />

      {/* Trocar Senha - Primeiro Login (protegido mas sem verificação de deve_trocar_senha) */}
      <Route
        path="/trocar-senha"
        element={
          <ProtectedRoute requirePasswordChange={false}>
            <TrocarSenha />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentos"
        element={
          <ProtectedRoute>
            <DocumentosList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentos/novo"
        element={
          <ProtectedRoute>
            <DocumentoForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentos/:id"
        element={
          <ProtectedRoute>
            <DocumentoDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentos/:id/editar"
        element={
          <ProtectedRoute>
            <DocumentoForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/itens"
        element={
          <ProtectedRoute>
            <ItensList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracoes"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verificacoes"
        element={
          <ProtectedRoute>
            <Verificacoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/envios"
        element={
          <ProtectedRoute>
            <Envios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/funcionarios"
        element={
          <ProtectedRoute>
            <Funcionarios />
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute>
            <SuperAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suporte"
        element={
          <ProtectedRoute>
            <Tickets />
          </ProtectedRoute>
        }
      />

      <Route
        path="/assinaturas"
        element={
          <ProtectedRoute>
            <Assinaturas />
          </ProtectedRoute>
        }
      />

      {/* Redirect root based on user type */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* 404 - Redirect based on user type */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

// Componente para redirecionar baseado no tipo de usuário
const RootRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.is_super_admin) {
    return <Navigate to="/super-admin" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
